import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCollection, getLabel, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';
import StockSupplierOverview from '../components/StockSupplierOverview';

const movementLabels = {
  INITIAL_STOCK: 'Stock initial',
  SUPPLY: 'Approvisionnement',
  RESERVE: 'Réservation',
  RELEASE: 'Libération',
  CONSUME: 'Sortie validée',
  ADJUST: 'Ajustement',
};

const movementColors = {
  INITIAL_STOCK: '#38bdf8',
  SUPPLY: '#22c55e',
  RESERVE: '#f59e0b',
  RELEASE: '#a78bfa',
  CONSUME: '#ef4444',
  ADJUST: '#64748b',
};

const movementChipColors = {
  INITIAL_STOCK: 'info',
  SUPPLY: 'success',
  RESERVE: 'warning',
  RELEASE: 'secondary',
  CONSUME: 'error',
  ADJUST: 'default',
};

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 3);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR');
}

function formatDiopter(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
}

function EmptyChart({ label = 'Aucune donnée.' }) {
  return (
    <Box sx={{ height: 280, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
      {label}
    </Box>
  );
}

function getMovementLabel(type) {
  return movementLabels[type] || type || '—';
}

function getMovementQuantitySign(type) {
  if (['SUPPLY', 'INITIAL_STOCK', 'RELEASE'].includes(type)) return '+';
  if (['RESERVE', 'CONSUME'].includes(type)) return '-';
  return '';
}

export default function StockMovementStatisticsPage() {
  const menuPage = useMenuPage('/statistiques/stock');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('vue') === 'verres' ? 'verres' : 'mouvements';
  const initialRange = useMemo(defaultDateRange, []);
  const [range, setRange] = useState(initialRange);
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams(range).toString();
      const [statsPayload, movementsPayload] = await Promise.all([
        requestJson(`/statistics/stock?${query}`),
        requestJson(`/stock-movements?${query}&limit=500`),
      ]);
      setStats(statsPayload);
      setMovements(getCollection(movementsPayload));
    } catch (err) {
      setError(err.message);
      setStats(null);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (tab === 'mouvements') loadData();
  }, [loadData, tab]);

  const filteredMovements = useMemo(
    () => movements.filter((movement) => (typeFilter ? movement.movementType === typeFilter : true)),
    [movements, typeFilter]
  );

  const gridRows = useMemo(
    () =>
      filteredMovements.map((movement) => ({
        ...movement,
        id: movement._id,
        movementLabel: getMovementLabel(movement.movementType),
        siteLabel: [movement.siteId?.code, getLabel(movement.siteId)].filter(Boolean).join(' - '),
        lensLabel: [movement.barcode, movement.supplier, movement.label].filter(Boolean).join(' - '),
        correction: `${formatDiopter(movement.sphere)} / ${formatDiopter(movement.cylinder)}`,
      })),
    [filteredMovements]
  );

  const movementTypeData = useMemo(
    () =>
      (stats?.byType || []).map((item) => ({
        id: item.type,
        label: getMovementLabel(item.type),
        value: item.quantity,
        color: movementColors[item.type] || '#64748b',
      })),
    [stats]
  );

  const timelineData = useMemo(() => {
    const periods = [...new Set((stats?.timeline || []).map((item) => item.period))].sort();
    return periods.map((period) => {
      const items = (stats?.timeline || []).filter((item) => item.period === period);
      return {
        period,
        approvisionnement: items.find((item) => item.type === 'SUPPLY')?.quantity || 0,
        reserve: items.find((item) => item.type === 'RESERVE')?.quantity || 0,
        sortie: items.find((item) => item.type === 'CONSUME')?.quantity || 0,
        liberation: items.find((item) => item.type === 'RELEASE')?.quantity || 0,
      };
    });
  }, [stats]);

  const topSites = useMemo(() => (stats?.bySite || []).slice(0, 10), [stats]);

  const totals = useMemo(() => {
    const byType = stats?.byType || [];
    return {
      supply: byType.find((item) => item.type === 'SUPPLY')?.quantity || 0,
      reserve: byType.find((item) => item.type === 'RESERVE')?.quantity || 0,
      consume: byType.find((item) => item.type === 'CONSUME')?.quantity || 0,
      totalEvents: stats?.total?.count || 0,
    };
  }, [stats]);

  const movementTypeCounts = useMemo(() => {
    const map = new Map();
    movements.forEach((movement) => {
      map.set(movement.movementType, (map.get(movement.movementType) || 0) + 1);
    });
    return map;
  }, [movements]);

  const columns = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Date mouvement',
        minWidth: 180,
        flex: 0.9,
        valueFormatter: (value) => formatDateTime(value),
      },
      {
        field: 'movementLabel',
        headerName: 'Mouvement',
        minWidth: 165,
        flex: 0.8,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            color={movementChipColors[params.row.movementType] || 'default'}
            sx={{ fontWeight: 850 }}
          />
        ),
      },
      {
        field: 'quantity',
        headerName: 'Qté',
        minWidth: 95,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 950,
              color:
                params.row.movementType === 'CONSUME'
                  ? 'error.main'
                  : params.row.movementType === 'SUPPLY'
                    ? 'success.main'
                    : params.row.movementType === 'RESERVE'
                      ? 'warning.main'
                      : 'text.primary',
            }}
          >
            {getMovementQuantitySign(params.row.movementType)}
            {params.value}
          </Typography>
        ),
      },
      { field: 'jobNumber', headerName: 'JOB', minWidth: 130, flex: 0.65 },
      { field: 'siteLabel', headerName: 'Atelier', minWidth: 180, flex: 0.9 },
      { field: 'lensLabel', headerName: 'Verre', minWidth: 320, flex: 1.5 },
      { field: 'correction', headerName: 'Sphère / Cyl.', minWidth: 130, flex: 0.6 },
      {
        field: 'availableQtyAfter',
        headerName: 'Dispo après',
        minWidth: 120,
        flex: 0.55,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reservedQtyAfter',
        headerName: 'Réservé après',
        minWidth: 130,
        flex: 0.6,
        align: 'center',
        headerAlign: 'center',
      },
      { field: 'reason', headerName: 'Origine', minWidth: 210, flex: 1 },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {menuPage.pageTitle}
          </Typography>
        </Box>
        {tab === 'mouvements' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            type="date"
            label="Du"
            value={range.from}
            onChange={(event) => setRange((current) => ({ ...current, from: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Au"
            value={range.to}
            onChange={(event) => setRange((current) => ({ ...current, to: event.target.value }))}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Rafraîchir
          </Button>
        </Stack>}
      </Stack>

      <Tabs
        value={tab}
        onChange={(_event, value) => setSearchParams(value === 'verres' ? { vue: 'verres' } : {}, { replace: true })}
        sx={{ borderBottom: 1, borderColor: 'divider', mt: '8px !important' }}
      >
        <Tab value="mouvements" label="Mouvements" sx={{ fontWeight: 800 }} />
        <Tab value="verres" label="Verres de stock" sx={{ fontWeight: 800 }} />
      </Tabs>

      {tab === 'verres' ? (
        <StockSupplierOverview />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              ['Événements', totals.totalEvents, 'Mouvements tracés', <Inventory2OutlinedIcon color="primary" />],
              ['Entrées', totals.supply, 'Approvisionnements', <TrendingUpRoundedIcon color="success" />],
              ['Réservations', totals.reserve, 'Commandes créées', <LockRoundedIcon color="warning" />],
              ['Sorties validées', totals.consume, 'Commandes terminées', <TrendingDownRoundedIcon color="error" />],
            ].map(([title, value, subtitle, icon]) => (
              <Card key={title} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  {icon}
                  <Typography variant="h4" sx={{ fontWeight: 950, mt: 1 }}>
                    {value}
                  </Typography>
                  <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {subtitle}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '0.85fr 1.15fr' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Répartition des mouvements
                </Typography>
                {movementTypeData.length ? (
                  <PieChart
                    height={320}
                    series={[
                      {
                        data: movementTypeData,
                        innerRadius: 55,
                        outerRadius: 115,
                        paddingAngle: 3,
                        cornerRadius: 5,
                      },
                    ]}
                    slotProps={{ legend: { position: { vertical: 'middle', horizontal: 'right' } } }}
                  />
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Évolution temporelle
                </Typography>
                {timelineData.length ? (
                  <LineChart
                    height={320}
                    dataset={timelineData}
                    xAxis={[{ scaleType: 'point', dataKey: 'period', label: 'Période' }]}
                    yAxis={[{ label: 'Quantité' }]}
                    series={[
                      { dataKey: 'approvisionnement', label: 'Approvisionné', color: '#22c55e' },
                      { dataKey: 'reserve', label: 'Réservé', color: '#f59e0b' },
                      { dataKey: 'sortie', label: 'Sortie validée', color: '#ef4444' },
                      { dataKey: 'liberation', label: 'Libéré', color: '#a78bfa' },
                    ]}
                    grid={{ horizontal: true }}
                  />
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                Top ateliers par mouvements
              </Typography>
              {topSites.length ? (
                <BarChart
                  height={300}
                  dataset={topSites.map((site) => ({
                    site: site.siteCode || site.siteName,
                    total: site.total,
                  }))}
                  xAxis={[{ scaleType: 'band', dataKey: 'site', label: 'Atelier' }]}
                  yAxis={[{ label: 'Quantité' }]}
                  series={[{ dataKey: 'total', label: 'Mouvements', color: '#38bdf8' }]}
                  grid={{ horizontal: true }}
                />
              ) : (
                <EmptyChart />
              )}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Journal des mouvements
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chaque ligne correspond à une variation historisée du stock.
                  </Typography>
                </Box>
                <Select
                  size="small"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                  sx={{ minWidth: 240 }}
                >
                  <MenuItem value="">Tous les mouvements ({movements.length})</MenuItem>
                  {Object.entries(movementLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label} ({movementTypeCounts.get(value) || 0})
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Box sx={{ height: 560, width: '100%' }}>
                <DataGrid
                  rows={gridRows}
                  columns={columns}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableRowSelectionOnClick
                  localeText={{
                    noRowsLabel: typeFilter
                      ? `Aucun mouvement "${getMovementLabel(typeFilter)}" sur la période.`
                      : 'Aucun mouvement sur la période.',
                  }}
                  sx={{
                    border: 0,
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
